/* eslint-disable simple-import-sort/imports */
import { Navigate, useRoutes } from 'react-router-dom'

import AuthLayout from '@/layouts/auth'
import DashboardLayout from '@/layouts/default'
import { ProtectedRoute, BillingGuard, RouteGuard } from '@qse/config'
import { QSE_ENTERPRISE_ID, AuthRoutes } from '@qse/constants'
import {
  LoginScreen,
  RegisterScreen,
  CompleteRegistrationPage,
  CompleteResellerRegistrationPage,
  ConfirmEmailPage,
  NewPasswordScreen,
  ForgotPasswordScreen,
  ResetPasswordScreen,
  TwoFAVerifyScreen,
  VerificationScreen,
  Setup2FAScreen,
  ProfileScreen,
  CheckInvitation,
  CheckResellerInvitation,
} from '@qse/auth-ui'
import {
  ResellersManagement,
  TenantManagement,
  UsersManagement,
  PlanManagement,
  StorageReport,
  TenantAnalytics,
} from '@/pages/management'
import { AppRoutesList } from './routes'
import Billing from '@/pages/billing/billing'
import Success from '@/pages/billing/success'
import Error from '@/pages/billing/error'
import SubscriptionManagement from '@/pages/management/subscription-managment'
import { UserRoleEnum } from '@qse/constants'
import { useUserStore } from '@qse/stores'

function AppRoutes() {
  const { user } = useUserStore()
  const userType = user?.userRole || ''
  const isTenantAdmin = userType === UserRoleEnum.TENANT_ADMIN
  const isSuperAdmin = userType === UserRoleEnum.SUPER_ADMIN
  const isResellerAdmin = userType === UserRoleEnum.RESELLER_ADMIN

  const routes = useRoutes([
    {
      path: '/',
      element: <Navigate replace to={AppRoutesList.login} />,
    },
    {
      element: (
        <ProtectedRoute type="public">
          <AuthLayout />
        </ProtectedRoute>
      ),
      children: [
        {
          path: AppRoutesList.login,
          element: <LoginScreen />,
        },
        {
          path: AppRoutesList.register,
          element: <RegisterScreen />,
        },
        {
          path: AppRoutesList.completeRegistration,
          element: <CompleteRegistrationPage />,
        },
        {
          path: AppRoutesList.confirmEmail,
          element: <ConfirmEmailPage />,
        },
        {
          path: AppRoutesList.newPassword,
          element: <NewPasswordScreen />,
        },
        {
          path: AppRoutesList.forgotPassword,
          element: <ForgotPasswordScreen />,
        },
        {
          path: AppRoutesList.resetPassword,
          element: <ResetPasswordScreen />,
        },
        {
          path: AppRoutesList.verification,
          element: <VerificationScreen />,
        },
        {
          path: AppRoutesList.twoFA,
          element: <Setup2FAScreen />,
        },
        {
          path: AppRoutesList.twoFAVerify,
          element: <TwoFAVerifyScreen />,
        },
      ],
    },

    {
      path: AppRoutesList.success,
      element: (
        <ProtectedRoute type="protected">
          <BillingGuard>
            <Success />
          </BillingGuard>
        </ProtectedRoute>
      ),
    },
    {
      path: AppRoutesList.error,
      element: (
        <>
          <>
            <Error />
          </>
        </>
      ),
    },
    {
      path: AppRoutesList.checkInvitation,
      element: <CheckInvitation />,
    },
    {
      path: AppRoutesList.checkResellerInvitation,
      element: <CheckResellerInvitation />,
    },
    {
      path: AuthRoutes.completeResellerRegistration,
      element: (
        <ProtectedRoute type="public">
          <AuthLayout />
        </ProtectedRoute>
      ),
      children: [
        {
          index: true,
          element: <CompleteResellerRegistrationPage />,
        },
      ],
    },
    {
      element: (
        <ProtectedRoute type="protected">
          <BillingGuard>
            <DashboardLayout />
          </BillingGuard>
        </ProtectedRoute>
      ),
      children: [
        ...(isTenantAdmin && user?.tenant?.resellerId !== QSE_ENTERPRISE_ID
          ? [
              {
                path: AppRoutesList.managementSubscription,
                element: <SubscriptionManagement />,
              },
            ]
          : []),

        {
          path: AppRoutesList.billing,
          element: <Billing />,
        },
        {
          path: AppRoutesList.profile,
          element: <ProfileScreen />,
        },
        {
          path: AppRoutesList.reseller,
          element: (
            <RouteGuard allowedRoles={[UserRoleEnum.SUPER_ADMIN]}>
              <ResellersManagement />
            </RouteGuard>
          ),
        },
        {
          path: AppRoutesList.tenants,
          element: (
            <RouteGuard allowedRoles={[UserRoleEnum.SUPER_ADMIN, UserRoleEnum.RESELLER_ADMIN]}>
              <TenantManagement />
            </RouteGuard>
          ),
        },
        {
          path: AppRoutesList.tenant,
          element: (
            <RouteGuard allowedRoles={[UserRoleEnum.SUPER_ADMIN, UserRoleEnum.RESELLER_ADMIN]}>
              <TenantManagement />
            </RouteGuard>
          ),
        },
        {
          path: AppRoutesList.users,
          element: (
            <RouteGuard allowedRoles={[UserRoleEnum.TENANT_ADMIN]}>
              <UsersManagement />
            </RouteGuard>
          ),
        },
        {
          path: AppRoutesList.tenantUsers,
          element: (
            <RouteGuard allowedRoles={[UserRoleEnum.SUPER_ADMIN, UserRoleEnum.RESELLER_ADMIN]}>
              <UsersManagement />
            </RouteGuard>
          ),
        },
        {
          path: AppRoutesList.plan,
          element: (
            <RouteGuard allowedRoles={[UserRoleEnum.SUPER_ADMIN, UserRoleEnum.RESELLER_ADMIN]}>
              <PlanManagement />
            </RouteGuard>
          ),
        },
        ...(isSuperAdmin || isResellerAdmin
          ? [
              {
                path: AppRoutesList.storageReport,
                element: <StorageReport />,
              },
              {
                path: AppRoutesList.tenantAnalytics,
                element: <TenantAnalytics />,
              },
            ]
          : []),

        {
          path: '*',
          element: (
            <Navigate
              replace
              state={{ showNotFoundError: true }}
              to={
                isTenantAdmin
                  ? AppRoutesList.users
                  : isResellerAdmin
                    ? AppRoutesList.tenants
                    : AppRoutesList.tenants
              }
            />
          ), // Redirect based on user role with error notification
        },
      ],
    },
  ])

  return routes
}

export default AppRoutes
